import React, { useState } from "react";
import ButtonPrimary from "../../shared/Button/ButtonPrimary";
import { useUserSessionStore } from "../../stores/UserSession.store";
import { IUser } from "../../@core/interfaces/IUser";
import { useReservations } from '../../hooks/collections/useReservations';

interface ServiceRatingProps {
  reservationId: string;
  initialRating?: number;
  initialComment?: string;
  onRatingSubmitted?: () => void;
  createdBy?: IUser;
}

const ServiceRating: React.FC<ServiceRatingProps> = ({
  reservationId,
  initialRating = 0,
  initialComment = "",
  onRatingSubmitted,
  createdBy
}) => {
  const [rating, setRating] = useState<number>(initialRating);
  const [comment, setComment] = useState<string>(initialComment);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { updateReservation } = useReservations({ loadData: false });
  const currentUser = useUserSessionStore((state) => state.user);

  const isCreator = createdBy && currentUser && createdBy.id === currentUser.id;

  const handleRatingChange = (newRating: number) => {
    setRating(newRating);
  };

  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setComment(e.target.value);
  };

  const handleSubmit = async () => {
    if (rating === 0) return;

    setIsSubmitting(true);
    try {
      await updateReservation(reservationId, {
        rating,
        comment
      });
      if (onRatingSubmitted) {
        onRatingSubmitted();
      }
    } catch (error) {
      console.error("Error submitting rating:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isCreator && initialRating > 0) {
    return (
      <div className="bg-neutral-50 dark:bg-neutral-800 rounded-2xl shadow-sm p-6">
        <div className="flex items-center mb-3">
          {[1, 2, 3, 4, 5].map((star) => (
            <span
              key={star}
              className={`text-2xl ${
                star <= initialRating
                  ? "text-yellow-400"
                  : "text-gray-300 dark:text-gray-600"
              }`}
            >
              ★
            </span>
          ))}
          <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
            {initialRating} stars
          </span>
        </div>
        {initialComment && (
          <div>
            <h4 className="text-md font-medium mb-1">Comment</h4>
            <p className="text-neutral-500 dark:text-neutral-400">{initialComment}</p>
          </div>
        )}
      </div>
    );
  }

  if (!isCreator && !initialRating) {
    return null;
  }

  return (
    <div className="bg-neutral-50 dark:bg-neutral-800 rounded-2xl shadow-sm p-6">
      <div className="flex items-center mb-4">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => handleRatingChange(star)}
            className={`text-2xl focus:outline-none ${
              star <= rating
                ? "text-yellow-400"
                : "text-gray-300 dark:text-gray-600"
            }`}
            aria-label={`Rate ${star} stars`}
          >
            ★
          </button>
        ))}
        <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
          {rating > 0 ? `${rating} stars` : "Select a rating"}
        </span>
      </div>
      <div className="mb-4">
        <label
          htmlFor="comment"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Your comment (optional)
        </label>
        <textarea
          id="comment"
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-neutral-800 dark:text-white"
          placeholder="Share your experience with this service..."
          value={comment}
          onChange={handleCommentChange}
        ></textarea>
      </div>
      <ButtonPrimary
        onClick={handleSubmit}
        disabled={isSubmitting}
        className="w-full"
      >
        {isSubmitting ? "Submitting..." : "Submit Rating"}
      </ButtonPrimary>
    </div>
  );
};

export default ServiceRating;
